import { useState } from "react";
import { Loader2, Save, KeyRound, UserCog, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AccountAdmin() {
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState({ name: user?.name || "", email: user?.email || "" });
  const [savingProfile, setSavingProfile] = useState(false);

  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [show, setShow] = useState(false);

  const field = "w-full rounded-lg border border-border px-3 py-2 text-sm text-ink outline-none focus:ring-2 focus:ring-primary";
  const lab = "text-xs font-semibold text-ink/60 block mb-1";

  const saveProfile = async () => {
    if (!profile.name || !profile.email) { toast.error("Nom et email requis"); return; }
    setSavingProfile(true);
    try {
      const r = await api.put("/auth/profile", profile);
      setUser({ ...user, ...r.data.user });
      toast.success("Profil mis à jour");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingProfile(false); }
  };

  const changePw = async () => {
    if (!pw.current_password || !pw.new_password) { toast.error("Remplissez tous les champs"); return; }
    if (pw.new_password.length < 8) { toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("La confirmation ne correspond pas"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Mot de passe modifié avec succès");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingPw(false); }
  };

  return (
    <div data-testid="account-page" className="max-w-2xl">
      <h1 className="font-heading text-3xl font-black text-ink">Mon compte</h1>
      <p className="text-ink/50 mt-1">Gérez vos informations personnelles et votre mot de passe.</p>

      <div className="mt-6 bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-2 mb-5"><UserCog size={18} className="text-primary" /><h2 className="font-heading font-bold text-ink text-lg">Profil</h2></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={lab}>Nom</label><input data-testid="account-name" className={field} value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></div>
          <div><label className={lab}>Email</label><input data-testid="account-email" type="email" className={field} value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} /></div>
        </div>
        <button onClick={saveProfile} disabled={savingProfile} data-testid="account-save-profile" className="mt-5 inline-flex items-center gap-2 bg-primary text-white font-bold px-6 py-2.5 rounded-full disabled:opacity-60">
          {savingProfile ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Enregistrer
        </button>
      </div>

      <div className="mt-6 bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2"><KeyRound size={18} className="text-primary" /><h2 className="font-heading font-bold text-ink text-lg">Mot de passe</h2></div>
          <button type="button" onClick={() => setShow((s) => !s)} className="text-xs text-ink/50 inline-flex items-center gap-1 hover:text-ink">
            {show ? <EyeOff size={14} /> : <Eye size={14} />} {show ? "Masquer" : "Afficher"}
          </button>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div><label className={lab}>Mot de passe actuel</label><input data-testid="account-current-pw" type={show ? "text" : "password"} className={field} value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} autoComplete="current-password" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={lab}>Nouveau mot de passe</label><input data-testid="account-new-pw" type={show ? "text" : "password"} className={field} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} autoComplete="new-password" placeholder="Min. 8 caractères" /></div>
            <div><label className={lab}>Confirmer</label><input data-testid="account-confirm-pw" type={show ? "text" : "password"} className={field} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} autoComplete="new-password" /></div>
          </div>
        </div>
        <button onClick={changePw} disabled={savingPw} data-testid="account-change-pw" className="mt-5 inline-flex items-center gap-2 bg-ink text-white font-bold px-6 py-2.5 rounded-full disabled:opacity-60">
          {savingPw ? <Loader2 className="animate-spin" size={16} /> : <KeyRound size={16} />} Modifier le mot de passe
        </button>
      </div>
    </div>
  );
}
